import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import DeviceEditDialog from '../components/DeviceEditDialog';
import { API_ENDPOINTS } from '../config/api';

interface Device {
  id: string;
  name: string;
  ip: string;
  status: 'online' | 'offline';
  lastSeen: string;
  location?: string;
  apks?: any[];
}

const DeviceManager: React.FC = () => {
  const { t } = useTranslation();
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newDevice, setNewDevice] = useState({
    name: '',
    ip: '',
    location: '',
  });
  const [editDevice, setEditDevice] = useState<Device | null>(null);

  const loadDevices = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.DEVICES);
      const data = await response.json();
      // API直接返回设备数组，不需要data.devices
      setDevices(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load devices:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDevices();
  }, []);

  const getStatusColor = (status: string) => {
    return status === 'online' ? 'success' : 'error';
  };

  const getStatusText = (status: string) => {
    return status === 'online' ? t('deviceManager.online') : t('deviceManager.offline');
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString('zh-CN');
  };

  const handleAddDevice = async () => {
    try {
      setLoading(true);
      
      // 先测试设备连接
      const testResponse = await fetch(`${API_ENDPOINTS.DEVICES}/test-connection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ip: newDevice.ip }),
      });

      if (!testResponse.ok) {
        alert('设备连接测试失败，请检查IP地址和网络连接');
        return;
      }

      // 连接测试通过，添加设备
      const response = await fetch(API_ENDPOINTS.DEVICES, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newDevice),
      });

      if (response.ok) {
        setAddDialogOpen(false);
        setNewDevice({ name: '', ip: '', location: '' });
        loadDevices();
        alert('设备添加成功！');
      } else {
        const errorData = await response.json();
        alert(`添加设备失败: ${errorData.error || '未知错误'}`);
      }
    } catch (error) {
      console.error('Failed to add device:', error);
      alert('添加设备失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDevice = async (deviceId: string) => {
    if (window.confirm(t('deviceManager.confirmDelete'))) {
      try {
        setLoading(true);
        const response = await fetch(`${API_ENDPOINTS.DEVICES}/${deviceId}`, {
          method: 'DELETE',
        });

        if (response.ok) {
          const result = await response.json();
          console.log('设备删除成功:', result);
          await loadDevices(); // 重新加载设备列表
        } else {
          const errorData = await response.json();
          console.error('删除设备失败:', errorData);
          alert(`删除设备失败: ${errorData.error || '未知错误'}`);
        }
      } catch (error) {
        console.error('Failed to delete device:', error);
        alert('删除设备失败，请重试');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditDevice = (device: Device) => {
    setEditDevice(device);
    setEditDialogOpen(true);
  };

  const handleUpdateDevice = async (deviceId: string, updates: Partial<Device>) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.DEVICES}/${deviceId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '更新设备失败');
      }

      await loadDevices();
    } catch (error: any) {
      throw error;
    }
  };

  const handleTestDevice = async (deviceId: string) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.DEVICES}/${deviceId}/test`, {
        method: 'POST',
      });

      if (response.ok) {
        loadDevices();
      }
    } catch (error) {
      console.error('Failed to test device:', error);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          {t('deviceManager.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          {t('deviceManager.addDevice')}
        </Button>
      </Box>

      {/* 设备统计卡片 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('deviceManager.totalDevices')}
              </Typography>
              <Typography variant="h4">
                {devices.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('deviceManager.onlineDevices')}
              </Typography>
              <Typography variant="h4" color="success.main">
                {devices.filter(d => d.status === 'online').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('deviceManager.offlineDevices')}
              </Typography>
              <Typography variant="h4" color="error.main">
                {devices.filter(d => d.status === 'offline').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('deviceManager.lastUpdate')}
              </Typography>
              <Typography variant="body2">
                {new Date().toLocaleTimeString('zh-CN')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 设备列表 */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('deviceManager.deviceName')}</TableCell>
                <TableCell>{t('deviceManager.ipAddress')}</TableCell>
                <TableCell>{t('deviceManager.status')}</TableCell>
                <TableCell>{t('deviceManager.lastSeen')}</TableCell>
                <TableCell>{t('deviceManager.location')}</TableCell>
                <TableCell>{t('deviceManager.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.map((device) => (
                <TableRow key={device.id}>
                  <TableCell>{device.name}</TableCell>
                  <TableCell>{device.ip}</TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusText(device.status)}
                      color={getStatusColor(device.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDateTime(device.lastSeen)}</TableCell>
                  <TableCell>{device.location || '-'}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleTestDevice(device.id)}
                      title={t('deviceManager.testConnection')}
                    >
                      <PlayIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleEditDevice(device)}
                      title={t('common.edit')}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteDevice(device.id)}
                      title={t('common.delete')}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 添加设备对话框 */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>添加新设备</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('deviceManager.deviceName')}
                value={newDevice.name}
                onChange={(e) => setNewDevice({ ...newDevice, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('deviceManager.deviceIP')}
                value={newDevice.ip}
                onChange={(e) => setNewDevice({ ...newDevice, ip: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('deviceManager.deviceLocation')}
                value={newDevice.location}
                onChange={(e) => setNewDevice({ ...newDevice, location: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>取消</Button>
          <Button onClick={handleAddDevice} variant="contained">添加</Button>
        </DialogActions>
      </Dialog>

      {/* 刷新按钮 */}
      <Box sx={{ position: 'fixed', bottom: 16, right: 16 }}>
        <IconButton
          color="primary"
          onClick={loadDevices}
          sx={{
            bgcolor: 'background.paper',
            boxShadow: 2,
            '&:hover': {
              bgcolor: 'background.paper',
            },
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* 设备编辑对话框 */}
      <DeviceEditDialog
        open={editDialogOpen}
        device={editDevice}
        onClose={() => {
          setEditDialogOpen(false);
          setEditDevice(null);
        }}
        onSave={handleUpdateDevice}
      />
    </Box>
  );
};

export default DeviceManager;
